import { useState } from "react";
import { usePublicClient, useWriteContract } from "wagmi";
import { isAddress, type Address } from "viem";
import { ERC721SimpleAbi } from "@atelier/abis";
import { byId } from "@/lib/chains";
import { ExplorerLink } from "@/components/ExplorerLink";
import { CollectionHeader } from "./CollectionHeader";
import { CollectionMetadataPanel } from "./CollectionMetadataPanel";
import { useChainMismatch } from "@/hooks/useChainMismatch";
import type { Collection } from "./types";

/** Dashboard row for ERC721Simple — one-by-one mintTo + collection metadata. */
export function SimpleRow({ c }: { c: Collection }) {
  const pc = usePublicClient({ chainId: c.chainId });
  const { writeContractAsync, isPending } = useWriteContract();
  const guard = useChainMismatch(c.chainId);
  const [to, setTo] = useState("");
  const [uri, setUri] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [lastTx, setLastTx] = useState<`0x${string}` | null>(null);
  const [showMeta, setShowMeta] = useState(false);
  const chain = byId(c.chainId);

  const mint = async () => {
    if (!isAddress(to) || !uri) return;
    setStatus("Signing…");
    try {
      const hash = await writeContractAsync({
        address: c.contractAddress,
        abi: ERC721SimpleAbi,
        functionName: "mintTo",
        args: [to as Address, uri],
        chainId: c.chainId,
      });
      setLastTx(hash);
      setStatus("Confirming…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setStatus("Minted.");
      setTo("");
      setUri("");
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  return (
    <div className="card flex flex-col gap-3">
      <CollectionHeader c={c} />
      {guard.wrong && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-medium">Wallet is on a different chain.</span>
          <button
            className="rounded border border-amber-500 px-2 py-0.5 text-[11px] font-semibold hover:bg-amber-500 hover:text-amber-50 disabled:opacity-50"
            onClick={guard.switchToTarget}
            disabled={guard.isSwitching}
          >
            {guard.isSwitching ? "Switching…" : `Switch to ${guard.targetName}`}
          </button>
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
        <input
          className="input h-9"
          placeholder="recipient 0x…"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <input
          className="input h-9"
          placeholder="tokenURI (e.g. ipfs://Qm…)"
          value={uri}
          onChange={(e) => setUri(e.target.value)}
        />
        <button
          className="btn-primary h-9 px-3 text-xs"
          disabled={isPending || !isAddress(to) || !uri || guard.wrong}
          title={guard.tooltip}
          onClick={() => void mint()}
        >
          Mint
        </button>
      </div>
      {status && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{status}</span>
          {lastTx && <ExplorerLink chain={chain} kind="tx" value={lastTx} label="view tx" />}
        </div>
      )}
      <div>
        <button
          className="btn-ghost h-7 px-2 text-xs"
          onClick={() => setShowMeta((v) => !v)}
          aria-expanded={showMeta}
        >
          {showMeta ? "Hide metadata" : "Royalty + contractURI"}
        </button>
      </div>
      {showMeta && (
        <CollectionMetadataPanel
          contractAddress={c.contractAddress}
          chainId={c.chainId}
          kind="simple"
          name={c.name}
          description={c.description}
          imageCid={c.imageCid}
          bannerCid={c.bannerCid}
        />
      )}
    </div>
  );
}
